import React, { useCallback, useEffect, useState } from "react";
import { Layout, PageHeader } from "@/components/Layout";
import { useLang } from "@/context/LanguageContext";
import { ShieldCheck, Eye, Pencil, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const ROLE_LABEL = {
  manager: "Super Admin", admin: "Super Admin", director: "Director",
  assistant: "Assistant (PA)", clerk: "Clerk", branch_manager: "Branch Manager",
};

export default function AccessControl() {
  const { t } = useLang();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/users")
      .then((r) => setUsers(r.data.users || []))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const setAccess = async (u, level) => {
    setSavingId(u.id);
    try {
      await api.put(`/admin/users/${u.id}/access`, { accessLevel: level });
      toast.success(`${u.email}: ${level === "view" ? "View only" : level === "full" ? "Full access" : "Role default"}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Layout>
      <PageHeader title={t("ac.title")} subtitle={t("ac.subtitle")} />

      <div className="mb-4 flex items-start gap-2 text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-3">
        <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0 text-slate-400" />
        <span><b>Full access</b> can add/edit/delete data. <b>View only</b> can look but not change anything. Assistants (PA) are always read-only.</span>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="access-control-table">
        {loading ? (
          <div className="p-8 text-slate-400 animate-pulse text-sm">Loading…</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-200">
                <th className="px-4 py-3 font-semibold">Account</th>
                <th className="px-4 py-3 font-semibold">Role</th>
                <th className="px-4 py-3 font-semibold">Scope</th>
                <th className="px-4 py-3 font-semibold">Current</th>
                <th className="px-4 py-3 font-semibold text-right">Set permission</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isSelf = u.id === user?.id;
                const isPA = u.role === "assistant";
                const busy = savingId === u.id;
                return (
                  <tr key={u.id} data-testid={`user-row-${u.email}`} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{u.name || u.email}</div>
                      <div className="text-xs text-slate-400 font-mono-fig">{u.email}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{ROLE_LABEL[u.role] || u.role}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs max-w-[240px]">{u.scope}</td>
                    <td className="px-4 py-3">
                      {u.canEdit ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium rounded-full border px-2 py-0.5 bg-emerald-50 text-emerald-700 border-emerald-200"><Pencil className="h-3 w-3" /> Full access</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-medium rounded-full border px-2 py-0.5 bg-slate-100 text-slate-600 border-slate-200"><Eye className="h-3 w-3" /> View only</span>
                      )}
                      {u.accessLevel !== "default" && <div className="text-[10px] text-slate-400 mt-0.5">manual override</div>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {busy && <Loader2 className="h-4 w-4 animate-spin text-slate-400 mr-1" />}
                        {isSelf ? (
                          <span className="text-xs text-slate-400">(your account)</span>
                        ) : isPA ? (
                          <span className="text-xs text-slate-400">read-only role</span>
                        ) : (
                          <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
                            <button data-testid={`set-view-${u.email}`} disabled={busy} onClick={() => setAccess(u, "view")}
                              className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${!u.canEdit ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>View only</button>
                            <button data-testid={`set-full-${u.email}`} disabled={busy} onClick={() => setAccess(u, "full")}
                              className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${u.canEdit ? "bg-white text-emerald-700 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>Full access</button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </Layout>
  );
}
